import { CONFIG } from "../config";

// Vertical bands of the canvas: staff (top), highway (middle), keyboard (pinned).
// Pure — the renderer reads these to place the staff and shrink the highway.
//
// Model: keyboard and staff are each sized to what they need (keyboard ∝ key
// width, staff ∝ its content) and clamped; the highway flexes to fill the middle.
// This keeps the keyboard piano-proportioned and the staff free of empty padding
// on any screen — instead of fixed percentage bands that bulk up or pad out.

export interface StageRects {
  staffTop: 0;
  staffHeight: number;
  highwayTop: number;
  highwayBottom: number;
  /** Top of the pinned keyboard band. */
  keyboardTop: number;
}

export interface StageOptions {
  staffEnabled: boolean;
  /** Split preset index — only consulted in page mode (flow fits one system). */
  staffSplit: number;
  /** Flow (single scrolling system) vs page (static rows). */
  flow: boolean;
  /** White-key width (px) — the keyboard band height is proportional to it. */
  whiteWidth: number;
}

/** Pinned-keyboard band height: proportional to white-key width (so narrow mobile
 *  keys aren't tall bars), floored for usability and capped at both a max px and a
 *  fraction of canvas height (so it can't dominate a short landscape viewport). */
export function keyboardBandHeight(canvasHeight: number, whiteWidth: number): number {
  const byWidth = whiteWidth * CONFIG.keyboardKeyAspect;
  const cap = Math.min(CONFIG.keyboardHeightPx, canvasHeight * CONFIG.keyboardMaxHeightFraction);
  return Math.max(CONFIG.keyboardMinHeightPx, Math.min(byWidth, cap));
}

/** Vertical extent of one grand-staff system plus its breathing room — the staff
 *  band is fitted to this in flow mode so there's no empty padding. */
export function staffContentHeight(): number {
  return CONFIG.staff.staffGapPx + CONFIG.staff.lineGapPx * 8 + 2 * CONFIG.staff.padPx;
}

/** Lay out the three bands: keyboard pinned at the bottom (∝ key width), staff at
 *  the top (fit to content in flow, preset fraction in page), highway in between. */
export function computeStageRects(_width: number, height: number, opts: StageOptions): StageRects {
  const keyboardTop = height - keyboardBandHeight(height, opts.whiteWidth);
  // A padding gap below the staff (before the highway, or the keyboard when the staff
  // fills the space) so the staff never abuts the falling notes / keys. Only when the
  // staff is shown — staff-off keeps the highway full to the top.
  const gap = opts.staffEnabled ? CONFIG.staff.belowStaffGapPx : 0;
  const usable = Math.max(0, keyboardTop - gap);

  let staffHeight = 0;
  if (opts.staffEnabled) {
    const content = Math.min(usable, staffContentHeight());
    if (opts.flow) {
      staffHeight = content; // one grand staff, fitted — no centering slack
    } else {
      const presets = CONFIG.staff.splitPresets;
      const idx = Math.max(0, Math.min(presets.length - 1, opts.staffSplit));
      const fraction = presets[idx]?.[1] ?? 0;
      staffHeight = fraction <= 0 ? 0 : Math.max(content, Math.min(usable, usable * fraction));
    }
  }

  // The highway begins below the staff + its gap (or at the very top when no staff).
  const highwayTop = staffHeight > 0 ? staffHeight + gap : 0;
  return {
    staffTop: 0,
    staffHeight,
    highwayTop,
    highwayBottom: keyboardTop,
    keyboardTop,
  };
}
